import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { api } from "@/lib/api";

const TenantContext = createContext({ tenant: null, loading: false });

/**
 * Resolves the visiting tenant by either path slug (/t/:slug) or
 * query parameter (?tenant=slug). Used to render white-labeled
 * login & marketing pages.
 */
export function TenantThemeProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    // Look for slug in path /t/<slug>/... or in ?tenant=
    let slug = params.slug || null;
    if (!slug) {
      const match = location.pathname.match(/^\/t\/([^/]+)/);
      if (match) slug = match[1];
    }
    if (!slug) {
      const q = new URLSearchParams(location.search);
      slug = q.get("tenant");
    }
    if (!slug) { setTenant(null); return; }
    setLoading(true);
    api.get(`/public/tenants/${slug}`)
      .then(({ data }) => setTenant(data))
      .catch(() => setTenant(null))
      .finally(() => setLoading(false));
  }, [location.pathname, location.search, params.slug]);

  // Inject CSS variables for theming
  useEffect(() => {
    const root = document.documentElement;
    if (tenant?.branding?.primary_color) {
      root.style.setProperty("--tenant-primary", tenant.branding.primary_color);
    } else {
      root.style.removeProperty("--tenant-primary");
    }
  }, [tenant]);

  return (
    <TenantContext.Provider value={{ tenant, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenantTheme = () => useContext(TenantContext);
